/**
 * The moderation queue: what people have sent through a collect link and the
 * owner has not yet said yes or no to.
 *
 * The page owns it, because the page has to know the count before anyone opens
 * a panel — a badge on the Circles button and on the dock is how an owner who
 * never opens Circles learns that somebody submitted. The panel's "Waiting for
 * you" section draws from the same object and takes entries off it as they are
 * approved or rejected, so the badge and the list never disagree.
 */
import type { PendingEntry } from '@circle/shared';
import { api } from '../app/api';
import { errorMessage } from './toast';

export interface PendingState {
  /** Undefined until the first answer is back. */
  entries: PendingEntry[] | undefined;
  /** Why there is no list, when the request failed. */
  error: string | undefined;
}

export class PendingQueue {
  private entries: PendingEntry[] | undefined;
  private error: string | undefined;
  private readonly watchers = new Set<(state: PendingState) => void>();

  constructor(private readonly calendarId: string) {}

  get state(): PendingState {
    return { entries: this.entries, error: this.error };
  }

  /** How many are waiting; zero while unknown, so nothing badges a guess. */
  get count(): number {
    return this.entries?.length ?? 0;
  }

  /** Ask the server. Safe to call again after an action elsewhere. */
  async load(): Promise<void> {
    try {
      this.entries = await api.listPending(this.calendarId);
      this.error = undefined;
    } catch (err) {
      this.error = errorMessage(err, 'Could not load submissions.');
    }
    this.announce();
  }

  /** One entry was approved or rejected, so it is no longer waiting. */
  remove(id: string): void {
    this.entries = this.entries?.filter((entry) => entry.id !== id);
    this.announce();
  }

  /** Hear about every change. Returns the way to stop. */
  watch(fn: (state: PendingState) => void): () => void {
    this.watchers.add(fn);
    return () => {
      this.watchers.delete(fn);
    };
  }

  destroy(): void {
    this.watchers.clear();
  }

  private announce(): void {
    const state = this.state;
    for (const watcher of this.watchers) watcher(state);
  }
}
